import { Navigate } from 'react-router';
import {
  PLATFORM_CAPABILITY_LABELS,
  isPlatformCapabilityLive,
  type PlatformCapability,
} from '@gatecheckpro/contracts';
import { Card, EmptyState } from '@gatecheckpro/design-system';
import { useViewer } from '../lib/session.tsx';

/**
 * The console landing.
 *
 * An operator with exactly one capability is sent straight to it. One with none
 * is told so plainly rather than being shown an empty dashboard that implies
 * something failed to load.
 */
const LANDING: Readonly<Partial<Record<PlatformCapability, string>>> = {
  OPS_ORGANIZER_ONBOARDING: '/onboarding',
  OPS_SECURITY: '/audit',
  OPS_PLATFORM_ADMIN: '/capability-grants',
};

export function NoCapabilityPage() {
  const viewer = useViewer();
  const held = viewer.platformCapabilities;

  const landings = held
    .map((capability) => LANDING[capability])
    .filter((path): path is string => path !== undefined);

  if (landings.length === 1) return <Navigate to={landings[0] as string} replace />;

  if (held.length === 0) {
    return (
      <EmptyState title="You have no operations capabilities">
        Your GateCheckPro account works, but nothing in this console has been granted to it. There
        is no general operator role — access here is one named capability at a time, and a platform
        administrator has to grant it explicitly.
      </EmptyState>
    );
  }

  return (
    <div className="gc-stack">
      <div className="gc-page-header">
        <span className="gc-eyebrow">Operations</span>
        <h1>What you can do</h1>
      </div>
      <div className="gc-grid">
        {held.map((capability) => (
          <Card key={capability} title={PLATFORM_CAPABILITY_LABELS[capability]}>
            <p className="gc-muted">
              {isPlatformCapabilityLive(capability)
                ? 'Available now.'
                : 'Granted, but the tooling for it is not built in this release.'}
            </p>
          </Card>
        ))}
      </div>
    </div>
  );
}
